import { useMemo } from 'react';
import { LIST_VIEW_CONSTANTS, PUNCTUATION_MARKS_CONSTANTS } from '../../common/constants/constants';
import { generateCustomSortBy } from '../helpers/productionUtils';
import { CUSTOM_SORT_OPTION_ID } from '../productionConstants';

/**
 * useListViewSort
 *
 * Determine the sort variables and optional client-side sortModel to apply when
 * querying productions for different list views.
 *
 * The hook returns: {
 *   shouldApplyDefaultSort: boolean,       // whether the default sortModel should be used
 *   customVariables: Record<string, any>,  // GraphQL variables (merged with provided customFilters)
 *   sortModel?: [string, string]           // optional tuple [field, direction] when using default sort
 * }
 *
 * @param sortPreference - user saved sort preference string (e.g. 'updatedOn_desc' or a custom option id)
 * @param currentView - active list view identifier (e.g. LIST_VIEW_CONSTANTS.listView)
 * @param workspacePreferenceValue - workspace level preference used to generate custom sorts
 * @param customFilters - existing GraphQL filter / variable object to merge with generated sortBy
 */
export const useListViewSort = (
    sortPreference: string,
    currentView: string,
    workspacePreferenceValue,
    customFilters
) => {
    return useMemo(() => {
        const [defaultSortModelField, defaultSortModelSort] = sortPreference?.includes(
            PUNCTUATION_MARKS_CONSTANTS.underscore
        )
            ? sortPreference.split(PUNCTUATION_MARKS_CONSTANTS.underscore)
            : ['updatedOn', 'desc'];

        // Rank order view has its own fixed API sortBy
        if (currentView === LIST_VIEW_CONSTANTS.rankOrderView) {
            const sortBy = 'rank_ASC,createdOn_ASC';
            return {
                shouldApplyDefaultSort: false,
                customVariables: {
                    ...customFilters,
                    sortBy
                }
            };
        }

        // Custom workspace sort applies for list, list preview and table views
        if (
            [LIST_VIEW_CONSTANTS.listPreview, LIST_VIEW_CONSTANTS.listView, LIST_VIEW_CONSTANTS.tableView].includes(
                currentView
            )
        ) {
            if (sortPreference === CUSTOM_SORT_OPTION_ID) {
                const sortBy = generateCustomSortBy(workspacePreferenceValue);
                return {
                    shouldApplyDefaultSort: false,
                    customVariables: {
                        ...customFilters,
                        sortBy
                    }
                };
            }
        }

        // Default fallback: use client-side sortModel
        return {
            shouldApplyDefaultSort: true,
            customVariables: {
                ...customFilters,
                sortBy: `${defaultSortModelField}_${defaultSortModelSort}`
            },
            sortModel: [defaultSortModelField, defaultSortModelSort]
        };
    }, [sortPreference, currentView, workspacePreferenceValue, customFilters]);
};
